import { Info } from 'lucide-react'

import { cn } from '@/lib/utils'
import styles from './DemoDataChip.module.scss'

/**
 * Marks a UI field whose value is currently mocked, placeholder, or rendered
 * without a wired backend source. This is the **only** sanctioned way to flag
 * demo data in the product: do not invent ad-hoc "data pending" pills, muted
 * dashes, or tooltip-only signals — render `<DemoDataChip />` instead.
 *
 * Every usage is picked up by `scripts/generate-demo-data-registry.mjs` and
 * surfaced in `docs/demo-data-registry.md`. That registry is the canonical
 * artefact we hand to the bank to scope which data sources still need to be
 * connected. When the bank wires a real source for a field, delete the
 * `<DemoDataChip>` usage and regenerate the registry; the entry will drop out
 * automatically.
 *
 * ## Props
 *
 * - `field` (required) — Dot-notation id of the data the bank still needs to
 *   provide. Use a stable, descriptive path that reads sensibly in the
 *   registry, e.g. `client.kyc.expiry_date`, `client.performance.mtd_pct`.
 *   Multiple usages can share the same `field` id when they render the same
 *   underlying piece of data in more than one place.
 *
 * - `note` (optional) — One-line description of what the data is and what
 *   the bank would need to provide. Surfaces in the registry next to the
 *   field id and powers the hover tooltip on the section variant.
 *
 * - `size` — `'inline'` (default) for the small pill next to a value/label,
 *   `'section'` for the larger outlined row used at card/section level.
 *
 * - `tight` — Inline variant only; shrinks the pill for use inside dense
 *   composites (small dashboards, allocation rows).
 */
export interface DemoDataChipProps {
  field: string
  note?: string
  size?: 'inline' | 'section'
  tight?: boolean
  className?: string
}

const LABEL = 'Dati demo'

export function DemoDataChip({
  field,
  note,
  size = 'inline',
  tight,
  className,
}: DemoDataChipProps) {
  const title = note ?? 'Sorgente dati in arrivo'
  const ariaLabel = `Dati demo: ${note ?? field}`

  if (size === 'section') {
    return (
      <div
        className={cn(styles.section, className)}
        role="status"
        aria-label={ariaLabel}
        data-demo-field={field}
      >
        <Info className={styles.sectionIcon} aria-hidden />
        <span className={styles.sectionLabel}>{LABEL}</span>
        {note ? <span className={styles.sectionNote}>— {note}</span> : null}
      </div>
    )
  }

  return (
    <span
      className={cn(styles.inline, tight && styles.inlineTight, className)}
      title={title}
      aria-label={ariaLabel}
      data-demo-field={field}
    >
      {LABEL}
    </span>
  )
}
